import React, { useEffect, useMemo, useState } from 'react';
import AdminSidebar from '../components/AdminSidebar';
import hygeaLogo from '../../assets/hygea.png';

import { API_BASE } from '../../config/runtime';

const formatBytes = (bytes) => {
  const n = Number(bytes || 0);
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(2)} MB`;
  return `${(n / (1024 * 1024 * 1024)).toFixed(2)} GB`;
};

const Backup = () => {
  const [backups, setBackups] = useState([]);
  const [isBackingUp, setIsBackingUp] = useState(false);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState({ type: '', message: '' });

  const loadBackups = async () => {
    setLoading(true);
    setStatus({ type: '', message: '' });
    try {
      const response = await fetch(`${API_BASE}/list_backups.php`);
      const data = await response.json();
      if (!data.success) {
        setStatus({ type: 'error', message: data.message || 'Failed to load backups' });
        setLoading(false);
        return;
      }
      setBackups(data.backups || []);
    } catch (error) {
      setStatus({ type: 'error', message: `Failed to load backups: ${error.message}` });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadBackups();
  }, []);

  const handleCreateBackup = async () => {
    setIsBackingUp(true);
    setStatus({ type: '', message: '' });
    try {
      const response = await fetch(`${API_BASE}/create_backup.php`, { method: 'POST' });
      const data = await response.json();
      if (!data.success) {
        setStatus({ type: 'error', message: data.message || 'Failed to create backup' });
        setIsBackingUp(false);
        return;
      }
      setStatus({ type: 'success', message: data.message || 'Backup created successfully' });
      await loadBackups();
    } catch (error) {
      setStatus({ type: 'error', message: `Failed to create backup: ${error.message}` });
    } finally {
      setIsBackingUp(false);
    }
  };

  const handleDownload = (fileName) => {
    window.open(`${API_BASE}/download_backup.php?file=${encodeURIComponent(fileName)}`, '_blank');
  };

  const handleDelete = async (fileName) => {
    const confirmed = window.confirm(`Delete backup file ${fileName}?`);
    if (!confirmed) return;
    setStatus({ type: '', message: '' });
    try {
      const response = await fetch(`${API_BASE}/delete_backup.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ file: fileName }),
      });
      const data = await response.json();
      if (!data.success) {
        setStatus({ type: 'error', message: data.message || 'Failed to delete backup' });
        return;
      }
      setStatus({ type: 'success', message: data.message || 'Backup deleted successfully' });
      await loadBackups();
    } catch (error) {
      setStatus({ type: 'error', message: `Failed to delete backup: ${error.message}` });
    }
  };

  const latestBackup = useMemo(() => (backups.length > 0 ? backups[0] : null), [backups]);

  return (
    <div className="flex flex-col sm:flex-row min-h-screen hygea-app-bg">
      <AdminSidebar />

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0">

        {/* ── Top bar ── */}
        <div className="bg-white shadow-sm px-4 sm:px-6 lg:px-8 py-3 sm:py-4 flex flex-wrap justify-between items-center gap-3">
          <h1 className="text-xl sm:text-2xl font-bold text-gray-800">Backup Management</h1>
          <img src={hygeaLogo} alt="Hygea Wellness Spa" className="h-9 sm:h-12" />
        </div>

        <div className="flex-1 p-4 sm:p-6 lg:p-8">
          <div className="rounded-2xl sm:rounded-3xl border border-[#e8d9cf] bg-white p-4 sm:p-6 lg:p-8 shadow-[0_14px_35px_rgba(0,0,0,0.08)]">

            {/* Status message */}
            {status.message && (
              <div className={`mb-5 rounded-xl border px-4 py-3 text-sm ${
                status.type === 'error'
                  ? 'border-red-200 bg-red-50 text-red-700'
                  : 'border-emerald-200 bg-emerald-50 text-emerald-700'
              }`}>
                {status.message}
              </div>
            )}

            {/* ── Stats grid: 1 col → 3 col ── */}
            <div className="mb-5 sm:mb-6 grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
              <div className="rounded-2xl border border-[#eadfd8] bg-[#fdfaf7] p-4 sm:p-5">
                <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[#b07b60]">Total Backups</p>
                <p className="mt-2 text-2xl sm:text-3xl font-bold text-gray-800">{backups.length}</p>
              </div>
              <div className="rounded-2xl border border-[#eadfd8] bg-[#fdfaf7] p-4 sm:p-5">
                <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[#b07b60]">Latest Backup</p>
                <p className="mt-2 truncate text-sm font-semibold text-gray-800">
                  {latestBackup ? latestBackup.name : 'No backups yet'}
                </p>
              </div>
              <div className="rounded-2xl border border-[#eadfd8] bg-[#fdfaf7] p-4 sm:p-5">
                <p className="text-xs font-semibold uppercase tracking-[0.16em] text-[#b07b60]">Latest Size</p>
                <p className="mt-2 text-xl sm:text-2xl font-bold text-gray-800">
                  {latestBackup ? formatBytes(latestBackup.size) : '--'}
                </p>
              </div>
            </div>

            {/* ── Create backup panel ── */}
            <div className="mb-6 sm:mb-8 rounded-2xl border border-[#eadfd8] bg-gradient-to-r from-[#fff6f1] to-[#fffdfb] p-5 sm:p-6">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-800">Create New Backup</h2>
              <p className="mt-1 text-sm text-gray-600">Generate a full SQL snapshot of your current Hygea database.</p>
              {/* Stack button + hint on mobile */}
              <div className="mt-4 sm:mt-5 flex flex-wrap items-center gap-3">
                <button
                  onClick={handleCreateBackup}
                  disabled={isBackingUp}
                  className={`rounded-xl px-5 sm:px-6 py-2.5 sm:py-3 text-sm font-semibold text-white shadow ${
                    isBackingUp
                      ? 'cursor-not-allowed bg-gray-400'
                      : 'bg-gradient-to-r from-[#B56E5B] to-[#A35F53] hover:brightness-105'
                  }`}
                >
                  {isBackingUp ? 'Creating Backup...' : 'Create Backup Now'}
                </button>
                {isBackingUp && (
                  <span className="text-sm text-[#8f6758]">Please wait, generating SQL file...</span>
                )}
              </div>
            </div>

            {/* ── Backup history header ── */}
            <div className="mb-3 flex items-center justify-between">
              <h2 className="text-base sm:text-lg font-bold text-gray-800">Backup History</h2>
              <button
                type="button"
                onClick={loadBackups}
                className="rounded-lg border border-[#d9c8bb] bg-white px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-[#f8f1ec]"
              >
                Refresh
              </button>
            </div>

            {/* ── Table — horizontal scroll on small screens ── */}
            <div className="overflow-x-auto rounded-2xl border border-[#e7d8ce]">
              <table className="w-full min-w-[560px] border-collapse">
                <thead>
                  <tr className="bg-[#F5EDE7]">
                    {['Backup File', 'Date & Time', 'Size', 'Status', 'Actions'].map((h) => (
                      <th key={h} className="px-4 sm:px-6 py-3 text-left text-xs font-bold tracking-wide text-gray-700 whitespace-nowrap">
                        {h.toUpperCase()}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="bg-white">
                  {!loading && backups.map((backup, index) => (
                    <tr
                      key={backup.name}
                      className={`h-14 sm:h-16 border-t border-[#f1e6df] transition-colors hover:bg-[#fff8f3] ${
                        index % 2 === 0 ? 'bg-white' : 'bg-[#fffdfa]'
                      }`}
                    >
                      {/* Truncate long filenames gracefully */}
                      <td className="px-4 sm:px-6 py-3 text-sm font-medium text-gray-700 max-w-[180px] sm:max-w-[260px] truncate">
                        {backup.name}
                      </td>
                      <td className="px-4 sm:px-6 py-3 text-sm text-gray-700 whitespace-nowrap">{backup.created_at}</td>
                      <td className="px-4 sm:px-6 py-3 text-sm text-gray-700 whitespace-nowrap">{formatBytes(backup.size)}</td>
                      <td className="px-4 sm:px-6 py-3 text-sm whitespace-nowrap">
                        <span className="rounded-full bg-emerald-100 px-2 sm:px-3 py-1 text-xs font-semibold text-emerald-700">
                          {backup.status || 'Completed'}
                        </span>
                      </td>
                      <td className="px-4 sm:px-6 py-3 text-sm">
                        <div className="flex flex-wrap gap-2">
                          <button
                            type="button"
                            onClick={() => handleDownload(backup.name)}
                            className="rounded-lg bg-[#1f7a8c] px-2.5 sm:px-3 py-1.5 text-xs font-semibold text-white hover:bg-[#176272] whitespace-nowrap"
                          >
                            Download
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDelete(backup.name)}
                            className="rounded-lg bg-[#dc2626] px-2.5 sm:px-3 py-1.5 text-xs font-semibold text-white hover:bg-[#b91c1c] whitespace-nowrap"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}

                  {loading && (
                    <tr>
                      <td colSpan={5} className="px-6 py-8 text-center text-sm text-gray-500">
                        Loading backups...
                      </td>
                    </tr>
                  )}

                  {!loading && backups.length === 0 && (
                    <tr>
                      <td colSpan={5} className="px-6 py-8 text-center text-sm text-gray-500">
                        No backups found. Create your first backup.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default Backup;
